import type { QixitAuthApi, QixitAuthResult } from '@shared/auth/types';
import apiService from './api';
import { authService } from './authService';
import { recordMfaCompletion } from '../utils/mfaPortability';
import { getOrCreateFingerprintHash } from '../utils/fingerprint';
import type { LoginResponse } from '../types';

const DEVICE_ID_KEY = 'qixitDeviceId';

function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `mb-${Date.now()}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export function toQixitAuthResult(response: LoginResponse): QixitAuthResult {
  if (response.requiresMfa && response.mfaSessionToken) {
    return {
      kind: 'mfa',
      userId: response.userId,
      mfaSessionToken: response.mfaSessionToken,
      preferredMethod: response.preferredMfaMethod,
      availableMethods: response.availableMfaMethods || [],
      availablePasskeys: response.availablePasskeys || [],
      mfaScopeCredentialId: response.mfaScopeCredentialId ?? null,
    };
  }
  if (response.requiresIpVerification && response.ipVerificationToken) {
    return {
      kind: 'ip',
      ipVerificationToken: response.ipVerificationToken,
      maskedEmail: response.maskedEmail,
    };
  }
  return {
    kind: 'success',
    token: response.accessToken,
    refreshToken: response.refreshToken,
    userId: response.userId,
    username: (response.loginHandle || response.username || '').trim() || response.username,
    raw: response,
  };
}

/** Mailbox proxies /api on its own origin, so the panel uses relative URLs here. */
export const mailboxAuthApi: QixitAuthApi = {
  apiBase: '',
  get: <T,>(path: string, params?: Record<string, string>) => apiService.get<T>(path, { params }),
  post: <T,>(path: string, body: unknown) => apiService.post<T>(path, body),
  login: async (username, password) => toQixitAuthResult(await authService.login({ username, password })),
  completeMfa: async ({ token, method, code, assertionResponse, availableMethods }) => {
    const response = await authService.completeMfa({ token, method, code, assertionResponse });
    // Tracks a non-passkey factor while passkeys exist (cross-browser nudge).
    recordMfaCompletion(method, availableMethods);
    return toQixitAuthResult(response);
  },
  verifyIp: async ({ token, code }) => toQixitAuthResult(await authService.verifyIpLogin({ token, code })),
  requestMfaEmail: (token) => authService.requestMfaEmail(token),
  getPasskeyAuthOptions: ({ userId, passkeyId, preferPlatform, preferHybrid, scopeCredentialId }) =>
    authService.getPasskeyAuthOptions(userId, passkeyId, preferPlatform, scopeCredentialId, preferHybrid),
  getDeviceId,
  getFingerprint: getOrCreateFingerprintHash,
};
